import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import CustomDropdown from '../../../custom/customDropdown';
import { AuthContext } from '../../../Header/auth - context';

/* ═══════════════════════════════════════════════════════════════════════════
   Ulazne fakture → Podešavanje troškova: vrste i podvrste troška (po firmi).

   Podvrsta nosi PODRAZUMEVANI PDV — stopu i povrat — koji se predlaže čim se
   podvrsta izabere na stavci ulazne fakture ili KO/KZ (osiguranje 0% bez
   povrata, gorivo u zemlji 20% sa povratom…). Vrsta se može vezati za vrstu
   troška ture, da se zna pod kojom vrstom trošak sa fakture ide u troškovnik.
   Brisanje je pasivizacija — stare stavke zadržavaju naziv.
   ═══════════════════════════════════════════════════════════════════════════ */

const dt = () => new Date().toLocaleString('sr-RS', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
}).replace(/,/g, '');

const izveziExcel = async (list, zaglavlja, redovi, naziv, company) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(list);
    const headerRow = worksheet.addRow(zaglavlja);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
        cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });
    redovi.forEach(r => {
        const row = worksheet.addRow(r);
        row.eachCell(cell => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
    });
    worksheet.columns.forEach(col => {
        let max = 10;
        col.eachCell(cell => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
        col.width = max + 2;
    });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `${naziv} - ${company?.companyName || ''} - ${dt()}.xlsx`);
};

/* Zajednički poziv procedure — Greška iz procedure dolazi kao message. */
const useProcedura = () => {
    const auth = useContext(AuthContext);
    return async (step, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/invoice/ulazne', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step, ...telo }),
        });
        const tekst = await r.text();
        const d = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(d.message || 'Error');
        return d;
    };
};

/* ─────────────────────────── VRSTE ─────────────────────────── */
const praznaVrsta = { naziv: '', id_vrsta_troska_ture: null };

const VrsteTab = ({ vrste, troskoviTure, osvezi, toast }) => {
    const { t } = useTranslation();
    const company = useSelector(state => state.company);
    const posalji = useProcedura();
    const [formData, setFormData] = useState(praznaVrsta);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setFormData({ naziv: row.Naziv || '', id_vrsta_troska_ture: row.id_vrsta_troska_ture ?? null });
            } else {
                setFormData(praznaVrsta);
                setFormSubmitted(false);
            }
            return next;
        });
    };

    const reset = () => { setFormData(praznaVrsta); setSelectedId(null); setFormSubmitted(false); };

    const handleSave = (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        if (!String(formData.naziv).trim()) return;
        posalji('SAVE_VRSTA', { id: selectedId || 0, ...formData })
            .then(r => {
                const nema = String(r.message || '').includes('Nema izmena');
                toast.current.show({ severity: nema ? 'warn' : 'success', summary: r.message, life: 3000 });
                if (!nema) { reset(); osvezi(); }
            })
            .catch(e2 => toast.current.show({ severity: 'error', summary: t('common.greska'), detail: e2.message, life: 4000 }));
    };

    const handleDelete = () => {
        confirmDialog({
            message: t('troskovi.potvrdaBrisanjaVrste'),
            acceptLabel: t('common.yes'), rejectLabel: t('common.no'),
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => posalji('DELETE_VRSTA', { id: selectedId })
                .then(r => { toast.current.show({ severity: 'success', summary: r.message, life: 3000 }); reset(); osvezi(); })
                .catch(e2 => toast.current.show({ severity: 'error', summary: t('common.greska'), detail: e2.message, life: 4000 })),
        });
    };

    const exportExcel = () => izveziExcel(t('troskovi.vrste'),
        [t('troskovi.vrsta'), t('troskovi.trosakTure'), t('troskovi.brojPodvrsta'), t('troskovi.brojStavki')],
        (vrste || []).map(r => [r.Naziv, r.TrosakTure || '', r.Podvrsta, r.Stavki]),
        t('troskovi.vrste'), company);

    const tableHeader = (
        <div className="flex align-items-center gap-2 export-buttons" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip={t('common.delete')} />}
            </div>
            <input type="search" placeholder={t('common.search')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div className="col-12">
            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <form autoComplete="off" onSubmit={handleSave}>
                        <div className="gridLicni">
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('troskovi.vrsta')}: <span className="iz-obavezno">*</span></label>
                                <InputText value={formData.naziv}
                                    className={formSubmitted && !String(formData.naziv).trim() ? 'p-invalid' : ''}
                                    onChange={e => setFormData(p => ({ ...p, naziv: e.target.value }))} />
                            </div>
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('troskovi.trosakTure')}:</label>
                                <CustomDropdown name="id_vrsta_troska_ture" optionLabel="label" showClear
                                    value={formData.id_vrsta_troska_ture} options={troskoviTure || []}
                                    placeholder={t('troskovi.phTrosakTure')}
                                    onChange={e => setFormData(p => ({ ...p, id_vrsta_troska_ture: e.target.value ?? null }))} />
                            </div>
                        </div>
                        <div className="formUnosZap buttonUnosZap">
                            <button type="submit" className="btn btn-success">
                                {selectedId ? t('common.save') : t('admin.unos')}
                            </button>
                        </div>
                    </form>
                </Card>
                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable value={vrste || []} paginator rows={10} globalFilter={globalFilter}
                        scrollable scrollHeight="50vh" header={tableHeader}
                        resizableColumns columnResizeMode="expand" className="turePregledTable"
                        emptyMessage={t('common.noData')}
                        rowClassName={r => String(r.id) === String(selectedId) ? { 'selected-row': true } : {}}>
                        <Column header={t('common.choose')} style={{ minWidth: '90px' }}
                            body={rowData => (
                                <Button label={t('common.choose')} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                            )} />
                        <Column field="Naziv" header={t('troskovi.vrsta')} sortable style={{ minWidth: '200px' }} />
                        <Column field="TrosakTure" header={t('troskovi.trosakTure')} sortable style={{ minWidth: '150px' }} />
                        <Column field="Podvrsta" header={t('troskovi.brojPodvrsta')} sortable style={{ minWidth: '110px' }} />
                        <Column field="Stavki" header={t('troskovi.brojStavki')} sortable style={{ minWidth: '110px' }} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

/* ─────────────────────────── PODVRSTE ─────────────────────────── */
const praznaPodvrsta = { id_vrsta_troska: null, naziv: '', stopaPDV: null, povratPDV: false };

const PodvrsteTab = ({ vrste, podvrste, osvezi, toast }) => {
    const { t } = useTranslation();
    const company = useSelector(state => state.company);
    const posalji = useProcedura();
    const [formData, setFormData] = useState(praznaPodvrsta);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    /* Filter po vrsti — 200 podvrsta u jednoj tabeli se ne pregleda. */
    const [filterVrsta, setFilterVrsta] = useState(null);

    const vrsteOpcije = useMemo(() => (vrste || []).map(v => ({ value: v.id, label: v.Naziv })), [vrste]);
    const prikazane = useMemo(() => (podvrste || []).filter(p => !filterVrsta || p.id_vrsta_troska === filterVrsta),
        [podvrste, filterVrsta]);

    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setFormData({
                    id_vrsta_troska: row.id_vrsta_troska,
                    naziv: row.Naziv || '',
                    stopaPDV: row.StopaPDV != null ? Number(row.StopaPDV) : null,
                    povratPDV: row.PovratPDV === 1,
                });
            } else {
                setFormData({ ...praznaPodvrsta, id_vrsta_troska: filterVrsta });
                setFormSubmitted(false);
            }
            return next;
        });
    };

    const reset = () => { setFormData({ ...praznaPodvrsta, id_vrsta_troska: filterVrsta }); setSelectedId(null); setFormSubmitted(false); };

    const handleSave = (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        if (!formData.id_vrsta_troska || !String(formData.naziv).trim()) return;
        posalji('SAVE_PODVRSTA', {
            id: selectedId || 0,
            ...formData,
            /* BIT kao 1/0 — procedura tako čita */
            povratPDV: formData.povratPDV ? 1 : 0,
        })
            .then(r => {
                const nema = String(r.message || '').includes('Nema izmena');
                toast.current.show({ severity: nema ? 'warn' : 'success', summary: r.message, life: 3000 });
                if (!nema) { reset(); osvezi(); }
            })
            .catch(e2 => toast.current.show({ severity: 'error', summary: t('common.greska'), detail: e2.message, life: 4000 }));
    };

    const handleDelete = () => {
        confirmDialog({
            message: t('troskovi.potvrdaBrisanjaPodvrste'),
            acceptLabel: t('common.yes'), rejectLabel: t('common.no'),
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => posalji('DELETE_PODVRSTA', { id: selectedId })
                .then(r => { toast.current.show({ severity: 'success', summary: r.message, life: 3000 }); reset(); osvezi(); })
                .catch(e2 => toast.current.show({ severity: 'error', summary: t('common.greska'), detail: e2.message, life: 4000 })),
        });
    };

    const daNe = (v) => (v === 1 ? t('common.yes') : v === 0 ? t('common.no') : '');

    const exportExcel = () => izveziExcel(t('troskovi.podvrste'),
        [t('troskovi.vrsta'), t('troskovi.podvrsta'), t('troskovi.stopaPDV'), t('troskovi.povratPDV'), t('troskovi.brojStavki')],
        prikazane.map(r => [r.Vrsta, r.Naziv, r.StopaPDV != null ? Number(r.StopaPDV) : '', daNe(r.PovratPDV), r.Stavki]),
        t('troskovi.podvrste'), company);

    const tableHeader = (
        <div className="flex align-items-center gap-2 export-buttons" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip={t('common.delete')} />}
                <div style={{ minWidth: '220px' }}>
                    <CustomDropdown name="filterVrsta" optionLabel="label" showClear value={filterVrsta}
                        options={vrsteOpcije} placeholder={t('troskovi.sveVrste')}
                        onChange={e => setFilterVrsta(e.target.value ?? null)} />
                </div>
            </div>
            <input type="search" placeholder={t('common.search')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div className="col-12">
            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <form autoComplete="off" onSubmit={handleSave}>
                        <div className="gridLicni">
                            <div className="col-12 md:col-4" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('troskovi.vrsta')}: <span className="iz-obavezno">*</span></label>
                                <CustomDropdown name="id_vrsta_troska" optionLabel="label"
                                    value={formData.id_vrsta_troska} options={vrsteOpcije}
                                    invalid={formSubmitted && !formData.id_vrsta_troska}
                                    placeholder={t('ulazne.phVrsta')}
                                    onChange={e => setFormData(p => ({ ...p, id_vrsta_troska: e.target.value }))} />
                            </div>
                            <div className="col-12 md:col-4" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('troskovi.podvrsta')}: <span className="iz-obavezno">*</span></label>
                                <InputText value={formData.naziv}
                                    className={formSubmitted && !String(formData.naziv).trim() ? 'p-invalid' : ''}
                                    onChange={e => setFormData(p => ({ ...p, naziv: e.target.value }))} />
                            </div>
                            <div className="col-12 md:col-2" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('troskovi.stopaPDV')}:</label>
                                <InputNumber value={formData.stopaPDV} mode="decimal" minFractionDigits={0}
                                    maxFractionDigits={2} suffix=" %" placeholder={t('troskovi.phBezPredloga')}
                                    onValueChange={e => setFormData(p => ({ ...p, stopaPDV: e.value }))} />
                            </div>
                            <div className="col-12 md:col-2 ulazne-kvacica" style={{ alignSelf: 'end' }}>
                                <Checkbox inputId="trPovratPDV" checked={!!formData.povratPDV}
                                    onChange={e => setFormData(p => ({ ...p, povratPDV: e.checked }))} />
                                <label htmlFor="trPovratPDV">{t('troskovi.povratPDV')}</label>
                            </div>
                        </div>
                        <div className="formUnosZap buttonUnosZap">
                            <button type="submit" className="btn btn-success">
                                {selectedId ? t('common.save') : t('admin.unos')}
                            </button>
                        </div>
                    </form>
                </Card>
                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable value={prikazane} paginator rows={10} globalFilter={globalFilter}
                        scrollable scrollHeight="50vh" header={tableHeader}
                        resizableColumns columnResizeMode="expand" className="turePregledTable"
                        emptyMessage={t('common.noData')}
                        rowClassName={r => String(r.id) === String(selectedId) ? { 'selected-row': true } : {}}>
                        <Column header={t('common.choose')} style={{ minWidth: '90px' }}
                            body={rowData => (
                                <Button label={t('common.choose')} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                            )} />
                        <Column field="Vrsta" header={t('troskovi.vrsta')} sortable style={{ minWidth: '180px' }} />
                        <Column field="Naziv" header={t('troskovi.podvrsta')} sortable style={{ minWidth: '200px' }} />
                        <Column field="StopaPDV" header={t('troskovi.stopaPDV')} sortable style={{ minWidth: '100px' }}
                            body={r => (r.StopaPDV != null ? `${Number(r.StopaPDV)} %` : '')} />
                        <Column field="PovratPDV" header={t('troskovi.povratPDV')} sortable style={{ minWidth: '100px' }}
                            body={r => daNe(r.PovratPDV)} />
                        <Column field="Stavki" header={t('troskovi.brojStavki')} sortable style={{ minWidth: '100px' }} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

/* ─────────────────────────── EKRAN ─────────────────────────── */
const TroskoviHome = () => {
    const { t } = useTranslation();
    const toast = useRef(null);
    const posalji = useProcedura();
    const [tab, setTab] = useState(0);
    const activeClass = 'activeButton';
    const [vrste, setVrste] = useState([]);
    const [podvrste, setPodvrste] = useState([]);
    const [troskoviTure, setTroskoviTure] = useState([]);

    const osvezi = () => {
        posalji('GET_VRSTE_TROSKA').then(r => setVrste(Array.isArray(r) ? r : [])).catch(() => {});
        posalji('GET_PODVRSTE_TROSKA').then(r => setPodvrste(Array.isArray(r) ? r : [])).catch(() => {});
    };

    useEffect(() => {
        osvezi();
        posalji('INIT').then(d => setTroskoviTure(d.TroskoviTureDB || [])).catch(() => {});
        // eslint-disable-next-line
    }, []);

    return (
        <div style={{ marginTop: '1%' }}>
            <Toast ref={toast} />
            <ConfirmDialog />
            {/* Podtabovi kao u ostatku modula Fakture (Ulazne fakture, Izvodi):
                dugmad .zaposleniShift u .ulazne-trakica-kartica, aktivno nosi id activeButton. */}
            <div className="ulazne-trakica-kartica">
                <Button id={tab === 0 && activeClass} label={t('troskovi.vrste')} style={{ fontSize: 'smaller' }}
                    className="mt-3 zaposleniShift" onClick={() => setTab(0)} />
                <Button id={tab === 1 && activeClass} label={t('troskovi.podvrste')} style={{ fontSize: 'smaller' }}
                    className="mt-3 zaposleniShift" onClick={() => setTab(1)} />
            </div>
            {tab === 0 && <VrsteTab vrste={vrste} troskoviTure={troskoviTure} osvezi={osvezi} toast={toast} />}
            {tab === 1 && <PodvrsteTab vrste={vrste} podvrste={podvrste} osvezi={osvezi} toast={toast} />}
        </div>
    );
};

export default TroskoviHome;
